import { useEffect } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { useRouter } from 'src/routes/hooks';

import { usePlan } from 'src/hooks/use-plan';

import { trackEvent, ANALYTICS_EVENTS } from 'src/utils/posthog';

import Iconify from '../iconify';

// ----------------------------------------------------------------------

// The accent colour of the pop-up, by the seller's current plan.
const PLAN_CONFIG = {
  starter: { color: '#7635dc' },
  pro: { color: '#ff5630' },
  power: { color: '#ff9800' },
};

export default function UpgradePopover ({
  open,
  anchorEl,
  onClose,
  title,
  description,
  benefits = [],
  icon = 'solar:star-bold',
  upgradeUrl = '/account#billing',
}) {
  const router = useRouter();
  const { usage } = usePlan();

  const currentPlan = usage?.plan || 'starter';
  const planConfig = PLAN_CONFIG[currentPlan];

  // `title` identifies which limit prompted the popover — it is the caller's own label,
  // not seller data. Fires on each open, not on every render.
  useEffect(() => {
    if (open) {
      trackEvent(ANALYTICS_EVENTS.PLAN_UPGRADE_POPOVER_SHOWN, { prompt: title, plan: currentPlan });
    }
  }, [open, title, currentPlan]);

  const handleUpgrade = () => {
    trackEvent(ANALYTICS_EVENTS.PLAN_UPGRADE_CLICKED, {
      source: 'upgrade_popover',
      prompt: title,
      plan: currentPlan,
    });
    onClose();
    router.push(upgradeUrl);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      PaperProps={{
        sx: {
          mt: 1.5,
          width: 360,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          p: 3,
          background: (theme) =>
            `linear-gradient(135deg, ${alpha(planConfig?.color || theme.palette.primary.main, 0.08)}, ${alpha(
              planConfig?.color || theme.palette.primary.main,
              0.02
            )})`,
        }}
      >
        <Stack spacing={2} alignItems="center" textAlign="center">
          {/* Icon */}
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: (theme) =>
                `linear-gradient(135deg, ${alpha(planConfig?.color || theme.palette.primary.main, 0.2)}, ${alpha(
                  planConfig?.color || theme.palette.primary.main,
                  0.1
                )})`,
            }}
          >
            <Iconify icon={icon} width={32} sx={{ color: planConfig?.color || 'primary.main' }} />
          </Box>

          {/* Title */}
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>

          {/* Description */}
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {description}
          </Typography>

          {/* Benefits List */}
          {benefits.length > 0 && (
            <Stack
              spacing={1}
              sx={{
                width: '100%',
                p: 2,
                borderRadius: 1.5,
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.8),
              }}
            >
              {benefits.map((benefit, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <Iconify icon="eva:checkmark-circle-2-fill" width={20} sx={{ color: 'success.main' }} />
                  <Typography variant="body2">{benefit}</Typography>
                </Stack>
              ))}
            </Stack>
          )}

          {/* CTA Buttons */}
          <Stack direction="row" spacing={1.5} sx={{ width: '100%', mt: 1 }}>
            <Button variant="outlined" color="inherit" onClick={onClose} sx={{ flex: 1 }}>
              Maybe Later
            </Button>
            <Button
              variant="contained"
              onClick={handleUpgrade}
              sx={{
                flex: 1,
                background: (theme) =>
                  `linear-gradient(135deg, ${planConfig?.color || theme.palette.primary.main}, ${alpha(
                    planConfig?.color || theme.palette.primary.main,
                    0.8
                  )})`,
                '&:hover': {
                  background: (theme) =>
                    `linear-gradient(135deg, ${alpha(planConfig?.color || theme.palette.primary.main, 0.9)}, ${alpha(
                      planConfig?.color || theme.palette.primary.main,
                      0.7
                    )})`,
                },
              }}
              startIcon={<Iconify icon="solar:crown-bold" />}
            >
              Upgrade Now
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Popover>
  );
}

UpgradePopover.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  benefits: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.node])),
  icon: PropTypes.string,
  upgradeUrl: PropTypes.string,
};
